import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { simpleDate } from "@/lib/utility/date-helper";
import { GoToHelper } from "@/lib/utility/goToHelper";
import { handleError } from "@/lib/utility/handleError";
import { SharingLinkFactory } from "@/lib/utility/sharingLinkFactory";
import { $t } from "@/lib/utility/t";
import { toast } from "@/lib/utility/toast";
import sharedContentService from "@/services/mrfiktiv/services/sharedContentService";
import {
  MrfiktivCreateSharedContentElementDtoGen,
  MrfiktivSharedContentViewmodelGen,
  MrfiktivTimestampGen,
  MrfiktivUpdateSharedContentElementDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { SharedContentDataAccessLayer } from "@/store/modules/access-layers/shared-content.access-layer";
import Vue from "vue";
import { IReference, Reference } from "./reference.entity";
import { Timestamp } from "./timestamp.entity";

@IsFilterable
class SharedContentEntity
  implements
    MrfiktivSharedContentViewmodelGen,
    Partial<IEntity<ISharedContent, MrfiktivUpdateSharedContentElementDtoGen>>
{
  id: string;

  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.sharedContent.isSystemGenerated"
  })
  isSystemGenerated?: boolean | undefined;

  partnerId: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.sharedContent.userId"
  })
  userId?: string | undefined;

  refId?: string | undefined;

  refType?: BackendResourceEnum;

  @FilterConfig({
    type: Reference
  })
  refs: IReference[];

  @FilterConfig({
    type: FilterTypes.DATE,
    displayName: "objects.sharedContent.validBy"
  })
  validBy: string | null;

  token: string;

  @FilterConfig({
    type: Timestamp
  })
  timestamp: MrfiktivTimestampGen;

  isLoading = false;

  isNotFound = false;

  title = "";

  description = "";

  reportId?: string | null | undefined;
  personIds?: string[] | null | undefined;
  companyIds?: string[] | null | undefined;
  contactIds: string[];
  emailAddresses?: string[] | null | undefined;

  get isUpdateable(): boolean {
    return true;
  }

  get isExpired() {
    if (!this.validBy) return false;

    return new Date(this.validBy) < new Date();
  }

  get url() {
    return new SharingLinkFactory(this, false).createUrlWithQuery;
  }

  get expirationReadable(): string {
    if (this.isLoading) {
      return $t("...");
    }

    if (this.isExpired) {
      return $t("views.PartnerSharedReport.expired");
    }

    if (this.validBy) {
      return $t("timeLine.ActivityTimeLineItemShare.validBy", { date: simpleDate(this.validBy) });
    }

    return $t("timeLine.ActivityTimeLineItemShare.validIndefinitely");
  }

  get expirationColor(): string {
    if (this.isExpired) {
      return "error";
    } else {
      return "success";
    }
  }

  constructor(sharedContent?: Partial<SharedContentEntity | MrfiktivSharedContentViewmodelGen>) {
    this.id = sharedContent?.id || "";
    this.isSystemGenerated = sharedContent?.isSystemGenerated || false;
    this.partnerId = sharedContent?.partnerId || "";
    this.userId = sharedContent?.userId;
    this.refId = sharedContent?.refId || "";
    this.refType = sharedContent?.refType as BackendResourceEnum | undefined;
    this.refs = Reference.filterDuplicates((sharedContent?.refs ?? []).map((ref) => new Reference(ref)));
    this.validBy = sharedContent?.validBy || null;
    this.token = sharedContent?.token || "";
    this.timestamp = sharedContent?.timestamp || {
      created: "",
      lastModified: ""
    };
    this.title = sharedContent?.title || $t("timeLine.ActivityTimeLineItemDocument.sharedDocument");
    this.description = sharedContent?.description || "";

    this.isLoading = (sharedContent as SharedContentEntity)?.isLoading || false;

    this.isNotFound = (sharedContent as SharedContentEntity)?.isNotFound || false;

    this.reportId = sharedContent?.reportId || null;
    this.personIds = sharedContent?.personIds || [];
    this.companyIds = sharedContent?.companyIds || [];
    this.contactIds = sharedContent?.contactIds || [];
    this.emailAddresses = sharedContent?.emailAddresses || [];
  }

  private map(sharedContent: MrfiktivSharedContentViewmodelGen) {
    if (sharedContent.id) this.id = sharedContent.id;
    if (sharedContent.isSystemGenerated) this.isSystemGenerated = sharedContent.isSystemGenerated;
    if (sharedContent.partnerId) this.partnerId = sharedContent.partnerId;
    if (sharedContent.refId) this.refId = sharedContent.refId;
    if (sharedContent.refType) this.refType = sharedContent.refType as BackendResourceEnum;
    if (sharedContent.refs)
      this.refs = Reference.filterDuplicates((sharedContent?.refs ?? []).map((ref) => new Reference(ref)));
    if (sharedContent.validBy) this.validBy = sharedContent.validBy;
    if (sharedContent.token) this.token = sharedContent.token;
    if (sharedContent.timestamp) this.timestamp = sharedContent.timestamp;
    if (sharedContent.userId) this.userId = sharedContent.userId;
    if (sharedContent.title)
      this.title = sharedContent.title || $t("timeLine.ActivityTimeLineItemDocument.sharedDocument");
    if (sharedContent.description) this.description = sharedContent.description;
    this.reportId = sharedContent?.reportId || null;
    this.personIds = sharedContent?.personIds || null;
    this.companyIds = sharedContent?.companyIds || null;
    this.contactIds = sharedContent?.contactIds || [];
  }

  async fetch(silent?: boolean): Promise<this> {
    this.isLoading = true;
    try {
      const fetched = await sharedContentService.get(this.partnerId, this.id);
      this.map(fetched);
      SharedContentDataAccessLayer.set(this);
    } catch (e) {
      if (silent) Vue.$log.error(e);
      else handleError(e);
      this.isNotFound = true;
    } finally {
      this.isLoading = false;
    }

    return this;
  }

  async delete(): Promise<void> {
    this.isLoading = true;
    await sharedContentService.delete(this.partnerId, this.id).catch(handleError);

    SharedContentDataAccessLayer.delete(this);

    this.isLoading = false;
  }

  copyLinkToClipboard(): void {
    try {
      new SharingLinkFactory(this, false).copyLinkToClipboard();
      Vue.$toast.info($t("components.partner.PartnerReportInitializeCard.linkCopied"));
    } catch (error) {
      Vue.$log.error(error);
      Vue.$toast.error($t("components.partner.PartnerReportInitializeCard.error"));
    }
  }

  async update() {
    this.isLoading = true;
    await sharedContentService
      .update(this.partnerId, this.id, {
        refs: Reference.filterDuplicates((this.refs ?? []) as IReference[]),
        validBy: this.validBy,
        title: this.title,
        description: this.description,
        reportId: this.reportId,
        personIds: this.personIds,
        companyIds: this.companyIds,
        emailAddresses: this.emailAddresses,
        contactIds: this.contactIds
      })
      .then((b) => this.map(b))
      .catch(handleError);

    SharedContentDataAccessLayer.set(this);

    this.isLoading = false;

    return this;
  }

  async updatePartial(dto: Partial<MrfiktivUpdateSharedContentElementDtoGen>) {
    if (dto.refs) {
      dto.refs = Reference.filterDuplicates((this.refs ?? []) as IReference[]);
    }

    this.isLoading = true;
    await sharedContentService
      .update(this.partnerId, this.id, dto)
      .then((b) => this.map(b))
      .catch(handleError);

    SharedContentDataAccessLayer.set(this);

    this.isLoading = false;

    return this;
  }

  /**
   * Adds a reference to the shared content entity.
   * You can specify whether to add it to the beginning (prepend = true) or end (default).
   * The references are then shared with the user.
   */
  async addRef(ref: IReference, prepend = false) {
    if (prepend) {
      this.refs.unshift(ref);
    } else {
      this.refs.push(ref);
    }

    this.isLoading = true;
    await sharedContentService
      .update(this.partnerId, this.id, {
        refs: Reference.filterDuplicates((this.refs ?? []) as IReference[])
      })
      .then((b) => this.map(b));
    SharedContentDataAccessLayer.set(this);

    this.isLoading = false;

    return this;
  }

  async deactivate() {
    this.validBy = new Date().toISOString();

    return this.update();
  }

  async create(): Promise<this> {
    const dto: MrfiktivCreateSharedContentElementDtoGen = {
      refs: Reference.filterDuplicates(this.refs),
      validBy: this.validBy,
      description: this.description,
      title: this.title,
      reportId: this.reportId,
      personIds: this.personIds,
      companyIds: this.companyIds,
      emailAddresses: this.emailAddresses,
      contactIds: this.contactIds
    };

    const res = await sharedContentService.create(this.partnerId, dto);

    this.map(res);
    SharedContentDataAccessLayer.set(this);

    return this;
  }

  copyUrl() {
    new SharingLinkFactory(this, false).copyLinkToClipboard();
    toast.info($t("components.partner.PartnerReportInitializeCard.linkCopied"));
  }

  /**
   * goTo helper for navigation, similar to RepairEstimate entity, but using GoToHelper
   */
  goTo(router: any) {
    return {
      detail: () => {
        if (this.partnerId && this.id && this.token) {
          return new GoToHelper(router).goToSharedContentAccessDetail(this.partnerId, this.id, this.token);
        }
      }
    };
  }
}
type ISharedContent = SharedContentEntity;
const SharedContent = Filter.createForClass(SharedContentEntity);

export { ISharedContent, SharedContent };
